<template>
    <v-dialog v-model="dialog" scrollable max-width="800px">
        <template v-slot:activator="{ on, attrs }">
            <v-btn elevation="0" outlined v-bind="attrs" v-on="on">
                <v-icon left>
                    mdi-account-multiple-plus
                </v-icon> 
                {{ $t('components.BulkCustomersForm.DialogButtonLabel') }}
            </v-btn>
        </template>
        <v-card>
            <v-card-title>{{ $t('components.BulkCustomersForm.DialogTitleText') }}</v-card-title>
            <v-divider/>
            <v-card-text style="max-height: 600px;">
                <v-form v-model="customers.complete" @submit.prevent="createCustomers">
                    <div class="mt-6 pl-6">
                        <v-file-input 
                            show-size
                            v-model="customers.source" 
                            :loading="loading"
                            :rules="[rules.fileInput]"
                            :label="$t('components.BulkCustomersForm.CustomersSourceLabel')"
                            @change="loadCustomers"/>
                        <small v-html="$t('components.BulkCustomersForm.CustomersSourceTemplate', { link: '/template.csv' })"/>
                    </div>

                    <div>
                        <span class="d-block mt-6 mb-4 text-subtitle-1">{{ $t('components.BulkCustomersForm.PreviewListTitle') }}</span>
                        <CustomersList 
                            :loading="loading"
                            :customerKey="'name'" 
                            :customers="customers.data"
                            @updated="updateCustomer"
                            @removed="removeCustomer"/>

                        <div class="d-block text-right mt-4 mb-6">
                            <v-btn 
                                type="submit" 
                                elevation="2" 
                                color="success" 
                                :loading="loading"
                                :disabled="!customers.complete">
                                <v-icon left>mdi-account-plus</v-icon>
                                {{ $t('components.BulkCustomersForm.AddCustomerButtonLabel') }}
                            </v-btn>
                        </div>
                    </div>
                </v-form>
            </v-card-text>
            <v-divider/>
            <v-card-actions>
                <v-btn elevation="0" text @click="dialog = false">
                    {{ $t('components.BulkCustomersForm.DialogCancelButtonLabel') }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
    
</template>

<script>
import Papa from 'papaparse';
import EventBus from '@/utils/EventBus';
import CustomersAPI from '@/utils/CustomersAPI';

import CustomersList from '@/components/customers/CustomersList';

/**
 * CreateMultipleCustomers allows to admins to create a bulk of new customers 
 * using as information user a uploadable CSV file. It also includes the feature
 * of hot-editting over the new customers before register them. It uses the 
 * CustomersAPI class to create the customers and Papa Parse library to parse
 * the CSV file (https://www.papaparse.com/).
 */
export default {
    name: 'CreateMultipleCustomers',
    data: () => ({
        loading: false,
        dialog: false,
        customers: {
            complete: true,
            source: null,
            data: []
        },
        rules: {
            fileInput: (file) => {
                return (!!file && (file.type === 'text/csv' || file.type === 'application/vnd.ms-excel') && file.size > 0) || 'Choose a non empty .CSV file.';
            }
        }
    }),
    methods: {
        loadCustomers() {
            if (this.customers.source) {
                this.loading = true;
                Papa.parse(this.customers.source, {
                    header: true,
                    skipEmptyLines: true,
                    complete: results => {
                        this.customers.data = results.data.map(res => {
                            res.clinics = !res.clinics || res.clinics === "" ? [] : res.clinics.split(',');
                            res.report = !res.report ? { frecuency: '', accumulated: false } : res.report;
                            res.app_version = !res.app_version ? [] : res.app_version.split(',');
                            return res;
                        });

                        if (results.errors.length > 0) {
                            console.log(results.errors)
                            EventBus.$emit('popup', this.$t('components.BulkCustomersForm.BadFormatInputMessage'));
                        }

                        this.loading = false;
                    }
                });
            } else this.customers.data = [];
        },
        async createCustomers() {
            this.loading = true;
            const [ success, result ] = await CustomersAPI.createCustomers(this.customers.data);
            const message = success ? `${this.customers.data.length} customers created.` : result;
            
            this.dialog = false;
            if (success) this.$emit('created');
            EventBus.$emit('popup', message);
            this.loading = false;
        },
        updateCustomer(index, data) {
            this.customers.data[index] = data;

            // Force to reload list
            this.customers.data = [...this.customers.data];
        },
        removeCustomer(index) {
            this.customers.data.splice(index, 1);
        }
    },
    components: {
        CustomersList
    }
}
</script>